const articleHeadingTextClassName = {
  h1: "text-[24px] leading-[110%] tracking-[-0.72px] sm:text-[32px] sm:tracking-[-0.96px] lg:text-[48px] lg:tracking-[-1.44px]",
  h2: "text-[20px] leading-[120%] tracking-[-0.6px] sm:text-[24px] sm:tracking-[-0.72px] lg:text-[32px] lg:tracking-[-0.96px]",
  h3: "text-[16px] leading-[120%] tracking-[-0.48px] lg:text-[24px] lg:tracking-[-0.72px]",
} as const;

const articleHeadingBaseClassName =
  "mb-0 w-full self-stretch font-poppins font-semibold text-primary-700 [&+p]:mt-2";

const articleHeadingOffsetClassName = "mt-4 lg:mt-6";

export const articleHeadingClassName = {
  h1: `${articleHeadingOffsetClassName} ${articleHeadingBaseClassName} ${articleHeadingTextClassName.h1}`,
  h2: `${articleHeadingOffsetClassName} ${articleHeadingBaseClassName} ${articleHeadingTextClassName.h2}`,
  h3: `${articleHeadingOffsetClassName} ${articleHeadingBaseClassName} ${articleHeadingTextClassName.h3}`,
} as const;

/** First heading under the eyebrow. Drops the in-article offset so the two sit together. */
export const articleOpeningHeadingClassName = {
  h1: `mt-0 ${articleHeadingBaseClassName} ${articleHeadingTextClassName.h1}`,
  h2: `mt-0 ${articleHeadingBaseClassName} ${articleHeadingTextClassName.h2}`,
  h3: `mt-0 ${articleHeadingBaseClassName} ${articleHeadingTextClassName.h3}`,
} as const;

/** 18px label above the headline, 8px gap to the first H1. */
export const articleEyebrowClassName = "mb-1 text-lg!";

/** Space between consecutive body paragraphs. */
export const articleParagraphStackClassName = "[&>p+p]:mt-2";

export const articleBodyClassName =
  "w-full self-stretch font-inter text-sm leading-normal font-normal tracking-normal sm:text-sm lg:text-lg";

export const articleLinkClassName =
  "font-normal text-text-action no-underline hover:underline";

export const articleSeparatorLineClassName =
  "mx-auto h-px w-full max-w-[720px] border-0 bg-[#E1E6F0]";

/** `1-` must render 1. Plate omits listStart on the first item. */
export const articleListNumber = (listStart?: number) => {
  if (!listStart || listStart < 1) {
    return 1;
  }
  return listStart;
};

export const articleListLayoutClassName =
  "mb-0 flex w-full list-none flex-col items-start gap-[16px] self-stretch p-0";

export const articleListClassName = `mt-[24px] ${articleListLayoutClassName}`;

export const articleListItemClassName = `${articleBodyClassName} m-0 flex list-none flex-row items-start gap-[16px] p-0 leading-2.75 text-black lg:leading-3 [&_p]:m-0 [&_p]:leading-2.75 lg:[&_p]:leading-3`;

export const articleOrderedIndexClassName =
  "flex size-[24px] shrink-0 items-center justify-center rounded-full bg-primary-100 font-inter text-[14px] leading-[22px] font-semibold text-primary-700";

export const articleBulletListLayoutClassName =
  "mb-0 w-full list-disc self-stretch p-0 pl-[1.5rem] [&+p]:mt-2";

export const articleBulletListClassName = `mt-[8px] ${articleBulletListLayoutClassName}`;

export const articleBulletListItemClassName = `${articleBodyClassName} m-0 p-0 [&::marker]:text-current [&_p]:m-0`;

export const articleQuoteSectionClassName =
  "mt-[40px] mb-0 flex w-full flex-col items-start gap-[16px] self-stretch";

export const articleQuoteBoxClassName =
  "box-border flex w-full flex-col items-start justify-center gap-[16px] self-stretch rounded-tl-[24px] rounded-tr-[24px] rounded-br-[24px] rounded-bl-none bg-[#F1ECFE] p-[24px]";

export const articleQuoteTextClassName =
  "m-0 w-full self-stretch font-poppins text-xl leading-tight font-semibold text-primary-600 sm:text-2xl lg:text-[32px]";

export const articleQuoteProfileClassName =
  "flex flex-row items-center gap-[16px]";

export const articleQuoteAvatarClassName =
  "aspect-square size-[48px] shrink-0 rounded-full bg-[#d3d3d3] object-cover";

export const articleQuoteNameClassName =
  "m-0 line-clamp-2 overflow-hidden font-inter text-[14px] leading-[22px] font-semibold tracking-normal text-black lg:text-[18px] lg:leading-[24px]";

export const articleQuoteDescriptionClassName =
  "m-0 line-clamp-2 overflow-hidden font-inter text-[14px] leading-[22px] font-normal tracking-normal text-[#627183] sm:text-sm";

export const articleImageFigureClassName =
  "my-[38px] flex w-full flex-col items-start self-stretch lg:my-6";

export const articleImageClassName =
  "mx-auto box-border flex aspect-[358/249] h-auto w-full max-w-full shrink-0 items-start overflow-hidden rounded-3xl sm:aspect-[603/340] sm:w-[603px] md:aspect-[758/427] md:w-[758px] lg:aspect-[820/462] lg:w-[820px]";

export const articleImageMediaClassName =
  "h-full w-full rounded-3xl object-cover object-center";

export const articleContentInsetClassName =
  "box-border w-full px-2 pt-16 pb-12 sm:px-6 sm:pt-16 lg:px-[159.5px] xl:px-[280px]";

export const articleYoutubeEmbedWrapClassName =
  "flex w-full flex-col items-start self-stretch min-[641px]:items-center max-[640px]:[&+p]:!mt-0";

export const articleYoutubeFrameClassName =
  "relative isolate box-border m-0 aspect-square w-full max-w-none shrink-0 self-start overflow-clip rounded-[24px] border border-[#00000033] bg-[#d3d3d3] bg-clip-padding bg-origin-border bg-center bg-cover bg-no-repeat min-[641px]:mx-auto min-[641px]:aspect-[187/98] min-[641px]:h-[285.089px] min-[641px]:w-[544px] min-[641px]:max-w-none md:aspect-[187/98] md:h-[352.168px] md:w-[672px] md:max-w-full md:self-stretch lg:h-auto lg:w-[min(100%,719px)] lg:max-w-[719px] lg:self-start xl:h-[376.799px] xl:w-[719px]";

export const articleYoutubePlayerCropClassName =
  "absolute inset-0 h-full w-full overflow-hidden rounded-[inherit]";

export const articleYoutubePlayerClassName =
  "absolute top-0 m-0 h-full border-0 max-[640px]:left-1/2 max-[640px]:w-[177.778%] max-[640px]:-translate-x-1/2 min-[641px]:left-0 min-[641px]:w-full min-[641px]:translate-x-0";

export const articleYoutubePlayButtonClassName =
  "absolute inset-0 m-0 h-full w-full cursor-pointer rounded-[inherit] border-0 bg-transparent p-0";

export const articleYoutubePlayIconClassName =
  "absolute top-1/2 left-1/2 w-[12%] min-w-10 max-w-20 -translate-x-1/2 -translate-y-1/2";
